// Display helpers for the Self-Hosted screen.

// Legacy "unlimited" plans have no sentinel in the API — by product decision we
// treat any allowance over 999 as unlimited (e.g. AppSumo Tier 4 = 10000).
export const UNLIMITED_THRESHOLD = 999;
export const isUnlimited = (license) => (license?.max_allowed_domains || 0) > UNLIMITED_THRESHOLD;

// Mask the middle of a license key, keeping the first and last groups readable.
// "a1b2c3d4-e5f6-7890-abcd-ef1234567890" -> "a1b2c3d4-••••-••••-••••-ef1234567890"
export const maskLicenseKey = (key) => {
  if (!key) return '';
  const parts = key.split('-');
  if (parts.length <= 2) {
    if (key.length <= 8) return '••••';
    return `${key.slice(0, 4)}••••${key.slice(-4)}`;
  }
  return parts.map((part, i) => (i === 0 || i === parts.length - 1 ? part : '••••')).join('-');
};

// "2 of 25 sites used" / "2 sites · unlimited" usage summary.
export const usageSummary = (license) => {
  const used = license?.domains_used || 0;
  if (isUnlimited(license)) return `${used} ${used === 1 ? 'site' : 'sites'} · unlimited`;
  return `${used}/${license?.max_allowed_domains || 0} sites`;
};

// A human plan label from product_ref + the first entitlement's tier.
// The API has no dedicated "plan" field, so this is derived.
export const planLabel = (license) => {
  const product = license?.product_ref === 'appsumo' ? 'AppSumo' : 'North Commerce';
  const tier = license?.entitlements?.[0]?.tier;
  if (!tier) return product;
  // "appsumo_tier_3" -> "Tier 3"
  const pretty = tier
    .replace(/^appsumo_/, '')
    .replace(/_/g, ' ')
    .replace(/\btier\b/i, 'Tier')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${product} · ${pretty}`;
};

// License status -> badge styling key + label.
const STATUS = {
  active: { label: 'Active', className: 'active' },
  pending: { label: 'Pending', className: 'pending' },
  blocked: { label: 'Blocked', className: 'blocked' },
  expired: { label: 'Expired', className: 'expired' },
};
export const licenseStatusBadge = (status) =>
  STATUS[status] || { label: status || 'Unknown', className: 'pending' };

export const isLicenseActive = (license) => license?.status === 'active';

// Bytes -> "2.4 MB" / "812 KB".
export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

// A stable palette pick for a domain's initial avatar.
export const paletteForDomain = (domain) => {
  const palettes = ['warm', 'sage', 'violet', 'blue'];
  const source = domain || '';
  const total = source.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return palettes[total % palettes.length];
};
